import React, { useState, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  TrendingUp, TrendingDown, Users, DollarSign, Target, Calendar,
  BarChart3, PieChart, Activity, Award, AlertCircle, CheckCircle,
  ArrowUp, ArrowDown, Filter, GitCompare, ChevronDown, LayoutGrid, History,
  Clock, Repeat, ShoppingBag, Zap, Trophy, Globe, Monitor, Smartphone, Tablet,
  Mail, Tag, AlertTriangle
} from 'lucide-react';
import * as api from '../services/api';
import Tooltip from '../components/ui/Tooltip';
import InfoTooltip from '../components/ui/InfoTooltip';

const Statistics = () => {
  const { state } = useData();
  const { settings } = useSettings();
  const [selectedPipeline, setSelectedPipeline] = useState('all');
  const [dateRange, setDateRange] = useState('month');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [comparisonType, setComparisonType] = useState('previous');
  const [stats, setStats] = useState(null);
  const [comparisonStats, setComparisonStats] = useState(null);
  const [monthlyClosures, setMonthlyClosures] = useState([]);
  const [behaviorPatterns, setBehaviorPatterns] = useState(null);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [serviceStats, setServiceStats] = useState([]);
  const [trackingStats, setTrackingStats] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [ratingStats, setRatingStats] = useState(null);
  const [commercialStats, setCommercialStats] = useState([]);
  const [loadingCommercials, setLoadingCommercials] = useState(false);
  const [lossReasonStats, setLossReasonStats] = useState(null);
  const [loadingLossReasons, setLoadingLossReasons] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('resumen');
  const [cierresSubTab, setCierresSubTab] = useState('resumen');
  const [wonDeals, setWonDeals] = useState([]);
  const [wonDealsTotal, setWonDealsTotal] = useState(0);
  const [wonDealsPage, setWonDealsPage] = useState(1);
  const [wonDealsTotalPages, setWonDealsTotalPages] = useState(1);
  const [loadingWonDeals, setLoadingWonDeals] = useState(false); // resumen, cierres, patrones, tracking, perdidas
  
  const primaryColor = settings?.primaryColor || '#00b30c';
  const currency = settings?.currency || 'COP';

  // Period labels
  const periodLabels = {
    week: 'Esta Semana',
    month: 'Este Mes',
    quarter: 'Este Trimestre',
    semester: 'Este Semestre',
    year: 'Este Año',
    all: 'Todo',
    custom: 'Personalizado'
  };

  const comparisonLabels = {
    week: 'Semana Anterior',
    month: 'Mes Anterior',
    quarter: 'Trimestre Anterior',
    semester: 'Semestre Anterior',
    year: 'Año Anterior'
  };

  // Fetch statistics from backend
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        // Pass 'all' directly when all pipelines selected - the API will handle it
        const pipelineParam = selectedPipeline;
        
        // Make parallel API calls for better performance
        const [response, closuresData, serviceData] = await Promise.all([
          api.getPipelineStatistics(pipelineParam, dateRange, customDateFrom, customDateTo),
          api.getDealsMonthlyStats(
            dateRange, 
            dateRange === 'custom' ? customDateFrom : null, 
            dateRange === 'custom' ? customDateTo : null,
            selectedPipeline
          ),
          api.getDealsStatsByService(
            dateRange,
            dateRange === 'custom' ? customDateFrom : null,
            dateRange === 'custom' ? customDateTo : null,
            selectedPipeline
          )
        ]);
        
        setStats(response);
        setMonthlyClosures(closuresData || []);
        setServiceStats(serviceData || []);
        
        if (compareMode) {
          const compResponse = await api.getPipelineStatisticsComparison(
            pipelineParam, 
            dateRange, 
            comparisonType,
            customDateFrom,
            customDateTo
          );
          setComparisonStats(compResponse);
        } else {
          setComparisonStats(null);
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedPipeline, dateRange, customDateFrom, customDateTo, compareMode, comparisonType]);

  // Fetch behavior patterns when patterns tab is active
  useEffect(() => {
    const fetchPatterns = async () => {
      if (activeTab === 'patrones') {
        setLoadingPatterns(true);
        setBehaviorPatterns(null); // Reset to trigger reload with new filters
        try {
          const patterns = await api.getBehaviorPatterns(
            dateRange, 
            selectedPipeline,
            dateRange === 'custom' ? customDateFrom : null,
            dateRange === 'custom' ? customDateTo : null
          );
          setBehaviorPatterns(patterns);
        } catch (error) {
          console.error('Error fetching behavior patterns:', error);
        } finally {
          setLoadingPatterns(false);
        }
      }
    };
    fetchPatterns();
  }, [activeTab, dateRange, selectedPipeline, customDateFrom, customDateTo]);

  // Fetch commercial statistics when resumen tab is active
  useEffect(() => {
    const fetchCommercialStats = async () => {
      if (activeTab === 'resumen') {
        setLoadingCommercials(true);
        try {
          const commercials = await api.getCommercialStatistics(
            dateRange,
            dateRange === 'custom' ? customDateFrom : null,
            dateRange === 'custom' ? customDateTo : null
          );
          setCommercialStats(commercials?.stats || []);
        } catch (error) {
          console.error('Error fetching commercial stats:', error);
          setCommercialStats([]);
        } finally {
          setLoadingCommercials(false);
        }
      }
    };
    fetchCommercialStats();
  }, [activeTab, dateRange, customDateFrom, customDateTo]);

  // Fetch loss reason statistics when perdidas tab is active
  useEffect(() => {
    const fetchLossReasonStats = async () => {
      if (activeTab === 'perdidas') {
        setLoadingLossReasons(true);
        setLossReasonStats(null);
        try {
          const data = await api.getLossReasonStatistics(
            dateRange,
            selectedPipeline,
            dateRange === 'custom' ? customDateFrom : null,
            dateRange === 'custom' ? customDateTo : null
          );
          setLossReasonStats(data);
        } catch (error) {
          console.error('Error fetching loss reason stats:', error);
        } finally {
          setLoadingLossReasons(false);
        }
      }
    };
    fetchLossReasonStats();
  }, [activeTab, dateRange, selectedPipeline, customDateFrom, customDateTo]);

  // Fetch tracking statistics when tracking tab is active
  useEffect(() => {
    const fetchTrackingStats = async () => {
      if (activeTab === 'tracking') {
        setLoadingTracking(true);
        setTrackingStats(null);
        try {
          const data = await api.getTrackingStatistics();
          setTrackingStats(data);
        } catch (error) {
          console.error('Error fetching tracking stats:', error);
        } finally {
          setLoadingTracking(false);
        }
      }
    };
    fetchTrackingStats();
  }, [activeTab]);

  // Fetch rating statistics when resumen tab is active and date range changes
  useEffect(() => {
    const fetchRatingStats = async () => {
      if (activeTab === 'resumen') {
        try {
          const data = await api.getLeadsStatsByRating(
            dateRange,
            dateRange === 'custom' ? customDateFrom : null,
            dateRange === 'custom' ? customDateTo : null
          );
          setRatingStats(data);
        } catch (error) {
          console.error('Error fetching rating stats:', error);
        }
      }
    };
    fetchRatingStats();
  }, [activeTab, dateRange, customDateFrom, customDateTo]);

  // Load won deals detail for cierres sub-tabs
  useEffect(() => {
    if (activeTab !== 'cierres' || cierresSubTab === 'resumen') return;
    const fetchWonDeals = async () => {
      setLoadingWonDeals(true);
      try {
        const data = await api.getWonDealsDetail(
          wonDealsPage, 20,
          selectedPipeline !== 'all' ? selectedPipeline : null,
          dateRange !== 'custom' ? dateRange : null,
          dateRange === 'custom' ? customDateFrom : null,
          dateRange === 'custom' ? customDateTo : null
        );
        setWonDeals(data.deals || []);
        setWonDealsTotal(data.total || 0);
        setWonDealsTotalPages(data.pages || 1);
      } catch (err) { console.error(err); }
      finally { setLoadingWonDeals(false); }
    };
    fetchWonDeals();
  }, [activeTab, cierresSubTab, wonDealsPage, selectedPipeline, dateRange, customDateFrom, customDateTo]);


  // Format full currency value
  const formatFullCurrency = (value) => {
    if (currency === 'COP') {
      return new Intl.NumberFormat('es-CO', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0) + ' COP';
    }
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  // Format currency using COP with tooltips
  const formatCurrency = (value, withTooltip = false) => {
    const fullValue = formatFullCurrency(value);
    
    if (currency === 'COP') {
      if (value >= 1000000000) {
        const short = (value / 1000000000).toFixed(1) + 'B COP';
        return withTooltip ? (
          <Tooltip content={fullValue}>
            <span className="cursor-help border-b border-dotted border-current">{short}</span>
          </Tooltip>
        ) : short;
      }
      if (value >= 1000000) {
        const short = (value / 1000000).toFixed(1) + 'M COP';
        return withTooltip ? (
          <Tooltip content={fullValue}>
            <span className="cursor-help border-b border-dotted border-current">{short}</span>
          </Tooltip>
        ) : short;
      }
      return fullValue;
    }
    return fullValue;
  };

  const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

  // Calculate change
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Comparison card component for clarity
  const ComparisonCard = ({ title, icon: Icon, iconBg, current, previous, format = 'number', inverse = false, tooltip }) => {
    const change = calculateChange(current, previous);
    const isPositive = inverse ? parseFloat(change) < 0 : parseFloat(change) > 0;
    
    // Format with tooltips for currency values
    const formatValue = (val, isCurrency) => {
      if (isCurrency) {
        return formatCurrency(val, true); // with tooltip
      }
      if (format === 'percent') {
        return formatPercent(val);
      }
      return val || 0;
    };

    const formattedCurrent = formatValue(current, format === 'currency');
    const formattedPrevious = formatValue(previous, format === 'currency');

    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-2.5 rounded-xl ${iconBg}`}>
            <Icon className="w-5 h-5" />
          </div>
          {compareMode && change !== null && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
              isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        
        {/* Title with tooltip */}
        <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1">
          {title}
          {tooltip && <InfoTooltip content={tooltip} />}
        </p>
        
        <div className="space-y-2">
          {/* Current Period */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {periodLabels[dateRange] || 'Actual'}
            </span>
            <span className="text-xl font-bold text-slate-900">{formattedCurrent}</span>
          </div>
          
          {/* Previous Period - only show in compare mode */}
          {compareMode && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-400">
                {comparisonStats?.periodLabel || comparisonLabels[dateRange]}
              </span>
              <span className="text-sm text-slate-500">{formattedPrevious}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Estadísticas</h1>
          <p className="text-slate-500 mt-1">Análisis de rendimiento por embudo</p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <select
            value={selectedPipeline}
            onChange={(e) => setSelectedPipeline(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white text-sm"
          >
            <option value="all">Todos los Embudos</option>
            {state.pipelines.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white text-sm"
          >
            <option value="week">Esta Semana</option>
            <option value="month">Este Mes</option>
            <option value="quarter">Este Trimestre</option>
            <option value="semester">Este Semestre</option>
            <option value="year">Este Año</option>
            <option value="all">Todo el Tiempo</option>
            <option value="custom">Rango Personalizado</option>
          </select>

          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium ${
              compareMode 
                ? 'border-transparent text-white' 
                : 'border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            style={compareMode ? { backgroundColor: primaryColor } : {}}
          >
            <GitCompare className="w-4 h-4" />
            {compareMode ? 'Comparando' : 'Comparar'}
          </button>
        </div>
      </div>

      {/* Custom Date Range */}
      {dateRange === 'custom' && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-400" />
            <span className="text-sm font-medium text-slate-600">Desde:</span>
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => setCustomDateFrom(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': primaryColor }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">Hasta:</span>
            <input
              type="date"
              value={customDateTo}
              onChange={(e) => setCustomDateTo(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2"
            />
          </div>
        </div>
      )}

      {/* Comparison Period Info */}
      {compareMode && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GitCompare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-blue-900">Modo Comparación Activo</p>
                <p className="text-xs text-blue-600">
                  {periodLabels[dateRange]} vs {comparisonStats?.periodLabel || comparisonLabels[dateRange]}
                </p>
              </div>
            </div>
            
            {/* Fechas de comparación */}
            <div className="flex items-center gap-4 bg-white rounded-lg px-4 py-2 border border-blue-200">
              <div className="text-center">
                <p className="text-xs text-slate-500 font-medium">Período Actual</p>
                <p className="text-sm font-bold text-blue-700">
                  {(() => {
                    const now = new Date();
                    let startDate, endDate;
                    
                    if (dateRange === 'week') {
                      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                      endDate = now;
                    } else if (dateRange === 'month') {
                      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                      endDate = now;
                    } else if (dateRange === 'quarter') {
                      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
                      startDate = new Date(now.getFullYear(), quarterStart, 1);
                      endDate = now;
                    } else if (dateRange === 'semester') {
                      const semesterStart = now.getMonth() < 6 ? 0 : 6;
                      startDate = new Date(now.getFullYear(), semesterStart, 1);
                      endDate = now;
                    } else if (dateRange === 'year') {
                      startDate = new Date(now.getFullYear(), 0, 1);
                      endDate = now;
                    } else if (dateRange === 'custom' && customDateFrom && customDateTo) {
                      startDate = new Date(customDateFrom);
                      endDate = new Date(customDateTo);
                    } else {
                      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                      endDate = now;
                    }
                    
                    const formatDate = (d) => d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
                    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
                  })()}
                </p>
              </div>
              
              <div className="text-slate-300 text-lg font-bold">vs</div>
              
              <div className="text-center">
                <p className="text-xs text-slate-500 font-medium">Período Comparado</p>
                <p className="text-sm font-bold text-orange-600">
                  {(() => {
                    const now = new Date();
                    let startDate, endDate;
                    
                    if (dateRange === 'week') {
                      if (comparisonType === 'previous') {
                        startDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
                        endDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                      } else {
                        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000 - 7 * 24 * 60 * 60 * 1000);
                        endDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
                      }
                    } else if (dateRange === 'month') {
                      if (comparisonType === 'previous') {
                        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                      } else {
                        startDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
                        endDate = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
                      }
                    } else if (dateRange === 'quarter') {
                      const quarterStart = Math.floor(now.getMonth() / 3) * 3;
                      if (comparisonType === 'previous') {
                        startDate = new Date(now.getFullYear(), quarterStart - 3, 1);
                        endDate = new Date(now.getFullYear(), quarterStart, 0);
                      } else {
                        startDate = new Date(now.getFullYear() - 1, quarterStart, 1);
                        endDate = new Date(now.getFullYear() - 1, quarterStart + 3, 0);
                      }
                    } else if (dateRange === 'semester') {
                      const semesterStart = now.getMonth() < 6 ? 0 : 6;
                      if (comparisonType === 'previous') {
                        startDate = new Date(now.getFullYear(), semesterStart - 6, 1);
                        endDate = new Date(now.getFullYear(), semesterStart, 0);
                      } else {
                        startDate = new Date(now.getFullYear() - 1, semesterStart, 1);
                        endDate = new Date(now.getFullYear() - 1, semesterStart + 6, 0);
                      }
                    } else if (dateRange === 'year') {
                      startDate = new Date(now.getFullYear() - 1, 0, 1);
                      endDate = new Date(now.getFullYear() - 1, 11, 31);
                    } else if (dateRange === 'custom' && customDateFrom && customDateTo) {
                      const from = new Date(customDateFrom);
                      const to = new Date(customDateTo);
                      const diff = to.getTime() - from.getTime();
                      if (comparisonType === 'previous') {
                        endDate = new Date(from.getTime() - 1);
                        startDate = new Date(endDate.getTime() - diff);
                      } else {
                        startDate = new Date(from.getTime() - 365 * 24 * 60 * 60 * 1000);
                        endDate = new Date(to.getTime() - 365 * 24 * 60 * 60 * 1000);
                      }
                    } else {
                      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
                    }
                    
                    const formatDate = (d) => d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: startDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
                    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
                  })()}
                </p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setComparisonType('previous')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  comparisonType === 'previous' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-blue-700 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                Período Anterior
              </button>
              <button
                onClick={() => setComparisonType('same_last_year')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  comparisonType === 'same_last_year' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-blue-700 border border-blue-200 hover:bg-blue-50'
                }`}
              >
                Mismo Período Año Anterior
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('resumen')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'resumen'
              ? 'bg-white shadow-sm text-slate-900'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <LayoutGrid className="w-4 h-4" />
          Resumen
        </button>
        <button
          onClick={() => setActiveTab('cierres')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'cierres'
              ? 'bg-white shadow-sm text-slate-900'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <History className="w-4 h-4" />
          Cierres Reales
        </button>
        <button
          onClick={() => setActiveTab('patrones')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'patrones'
              ? 'bg-white shadow-sm text-slate-900'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Activity className="w-4 h-4" />
          Patrones
        </button>
        <button
          onClick={() => setActiveTab('tracking')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'tracking'
              ? 'bg-white shadow-sm text-slate-900'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Target className="w-4 h-4" />
          Origen de Leads
        </button>
        <button
          onClick={() => setActiveTab('perdidas')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'perdidas'
              ? 'bg-white shadow-sm text-slate-900'
              : 'text-slate-600 hover:text-slate-900'
          }`}
          data-testid="loss-reasons-tab"
        >
          <AlertTriangle className="w-4 h-4" />
          Razones de Pérdida
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'resumen' ? (
        <>
      {/* KPI Cards - Clearer Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <ComparisonCard
          title="Total Leads"
          icon={Users}
          iconBg="bg-indigo-50 text-indigo-600"
          current={stats?.totalLeads}
          previous={comparisonStats?.totalLeads}
          tooltip="Total de leads (personas que dejaron sus datos) creados en el período seleccionado. Se cuentan por fecha de creación."
        />
        <ComparisonCard
          title="Negocios Ganados"
          icon={CheckCircle}
          iconBg="bg-green-50 text-green-600"
          current={stats?.closedWon}
          previous={comparisonStats?.closedWon}
          tooltip="Negocios que se movieron a estado 'ganado' durante el período. Se cuenta por fecha de cierre real (actualCloseDate), no por fecha de creación."
        />
        <ComparisonCard
          title="Negocios Perdidos"
          icon={AlertCircle}
          iconBg="bg-red-50 text-red-600"
          current={stats?.closedLost}
          previous={comparisonStats?.closedLost}
          inverse={true}
          tooltip="Negocios que se movieron a estado 'perdido' durante el período. Indicador inverso: un valor menor es mejor."
        />
        <ComparisonCard
          title="Valor Ganado"
          icon={DollarSign}
          iconBg="bg-yellow-50 text-yellow-600"
          current={stats?.totalClosedValue}
          previous={comparisonStats?.totalClosedValue}
          format="currency"
          tooltip="Sumatoria de todos los valores de los negocios cerrados como ganados en el período."
        />
      </div>

      {/* Additional Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <ComparisonCard
          title="Tasa Conversión Ganados"
          icon={Target}
          iconBg="bg-purple-50 text-purple-600"
          current={stats?.conversionRate}
          previous={comparisonStats?.conversionRate}
          format="percent"
          tooltip="Porcentaje de negocios ganados sobre el total de negocios cerrados (ganados + perdidos). Fórmula: (Negocios Ganados / Total Cerrados) × 100"
        />
        <ComparisonCard
          title="Tasa de Pérdida"
          icon={TrendingDown}
          iconBg="bg-red-50 text-red-600"
          current={stats?.lossRate}
          previous={comparisonStats?.lossRate}
          format="percent"
          inverse={true}
          tooltip="Porcentaje de negocios perdidos sobre el total cerrados. Fórmula: (Negocios Perdidos / Total Cerrados) × 100. Indicador inverso: menor es mejor."
        />
        <ComparisonCard
          title="Valor Promedio Deal"
          icon={DollarSign}
          iconBg="bg-green-50 text-green-600"
          current={stats?.avgDealSize}
          previous={comparisonStats?.avgDealSize}
          format="currency"
          tooltip="Valor promedio por negocio ganado. Fórmula: Valor Total Ganado / Número de Negocios Ganados"
        />
        <ComparisonCard
          title="Días para Cerrar"
          icon={Calendar}
          iconBg="bg-blue-50 text-blue-600"
          current={stats?.avgDaysToClose}
          previous={comparisonStats?.avgDaysToClose}
          inverse={true}
          tooltip="Promedio de días desde la creación del negocio hasta que se cerró como ganado. Indicador inverso: menos días es mejor."
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads por Mes */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" style={{ color: primaryColor }} />
            Nuevos Leads por Mes
            <InfoTooltip content="Cantidad de leads nuevos creados cada mes en el período seleccionado. Incluye todos los leads del embudo, independientemente de su etapa actual." />
          </h3>
          <div className="space-y-3">
            {stats?.leadsByMonth?.map((item, index) => {
              const maxCount = Math.max(...(stats?.leadsByMonth?.map(m => m.count) || [1]));
              const compItem = comparisonStats?.leadsByMonth?.[index];
              return (
                <div key={index} className="flex items-center gap-3">
                  <span className="text-sm text-slate-600 w-10 font-medium">{item.month}</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
                    {compareMode && compItem && (
                      <div
                        className="absolute h-full rounded-full opacity-40 border-2 border-dashed border-slate-400"
                        style={{ width: `${(compItem.count / maxCount) * 100}%` }}
                      />
                    )}
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${(item.count / maxCount) * 100}%`,
                        backgroundColor: primaryColor
                      }}
                    />
                  </div>
                  <div className="w-20 text-right">
                    <span className="text-sm font-bold text-slate-900">{item.count}</span>
                    {compareMode && compItem && (
                      <span className="text-xs text-slate-400 ml-1">({compItem.count})</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {compareMode && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: primaryColor }}></div>
                {periodLabels[dateRange]}
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded border-2 border-dashed border-slate-400"></div>
                {comparisonStats?.periodLabel || comparisonLabels[dateRange]}
              </span>
            </div>
          )}
        </div>

        {/* Embudo de Conversión */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" style={{ color: primaryColor }} />
            Embudo de Conversión
            <InfoTooltip content="Distribución de leads por cada etapa del embudo de ventas. Muestra cuántos leads hay actualmente en cada etapa y el porcentaje de conversión respecto a la primera etapa." />
          </h3>
          <div className="space-y-4">
            {stats?.funnelData?.map((stage, index) => {
              const maxCount = stats.funnelData[0]?.count || 1;
              const width = (stage.count / maxCount) * 100;
              const compStage = comparisonStats?.funnelData?.[index];
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{stage.name}</span>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-slate-900">{stage.count}</span>
                      <span className="text-xs text-slate-500 ml-1">({formatPercent(stage.percentage)})</span>
                      {compareMode && compStage && (
                        <span className="text-xs text-slate-400 ml-2">vs {compStage.count}</span>
                      )}
                    </div>
                  </div>
                  <div className="h-9 bg-slate-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full rounded-lg flex items-center justify-center transition-all duration-700"
                      style={{ 
                        width: `${Math.max(width, 15)}%`,
                        backgroundColor: stage.color || primaryColor
                      }}
                    >
                      <span className="text-white text-xs font-medium drop-shadow">{formatCurrency(stage.value)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Oportunidades */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-yellow-500" />
            Top Oportunidades
          </h3>
          <div className="space-y-3">
            {stats?.topOpportunities?.map((lead, index) => (
              <div key={lead.id} className="flex items-center gap-3 p-3 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm"
                  style={{ backgroundColor: index === 0 ? '#f59e0b' : index === 1 ? '#9ca3af' : '#cd7f32' }}>
                  {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{lead.company}</p>
                  <p className="text-xs text-slate-500 truncate">{lead.name}</p>
                </div>
                <span className="text-sm font-bold" style={{ color: primaryColor }}>
                  {formatCurrency(lead.value)}
                </span>
              </div>
            ))}
            {(!stats?.topOpportunities || stats.topOpportunities.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">Sin oportunidades en este período</p>
            )}
          </div>
        </div>

        {/* Cierres Recientes */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            Cierres Recientes
            <InfoTooltip content="Últimos 5 negocios ganados en el período seleccionado, ordenados por fecha de cierre." />
          </h3>
          <div className="space-y-3">
            {stats?.recentClosures?.map((deal) => (
              <div key={deal.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{deal.name || deal.empresaName || 'Sin nombre'}</p>
                  <p className="text-xs text-slate-500">{deal.actualCloseDate ? new Date(deal.actualCloseDate).toLocaleDateString('es-ES') : '-'}</p>
                </div>
                <span className="text-sm font-bold text-green-600">
                  {formatCurrency(deal.totalValue || 0)}
                </span>
              </div>
            ))}
            {(!stats?.recentClosures || stats.recentClosures.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">Sin cierres en este período</p>
            )}
          </div>
        </div>

        {/* Pérdidas Recientes */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Pérdidas Recientes
            <InfoTooltip content="Últimos 5 negocios perdidos en el período seleccionado, ordenados por fecha de cierre." />
          </h3>
          <div className="space-y-3">
            {stats?.recentLosses?.map((deal) => (
              <div key={deal.id} className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{deal.name || deal.empresaName || 'Sin nombre'}</p>
                  <p className="text-xs text-slate-500">{deal.actualCloseDate ? new Date(deal.actualCloseDate).toLocaleDateString('es-ES') : '-'}</p>
                </div>
                <span className="text-sm font-bold text-red-600">
                  {formatCurrency(deal.totalValue || 0)}
                </span>
              </div>
            ))}
            {(!stats?.recentLosses || stats.recentLosses.length === 0) && (
              <p className="text-sm text-slate-500 text-center py-4">Sin pérdidas en este período</p>
            )}
          </div>
        </div>
      </div>

      {/* Leads por Calificación */}
      {ratingStats && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            Leads por Calificación
            <InfoTooltip content="Distribución de leads según su calificación de estrellas (1-5). Los leads sin calificar aparecen como 'Sin calificar'." />
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Sin calificar */}
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <div className="flex justify-center mb-2">
                <span className="text-slate-400 text-sm">Sin calificar</span>
              </div>
              <p className="text-2xl font-bold text-slate-600">{ratingStats.byRating[0] || 0}</p>
            </div>
            {/* 1-5 estrellas */}
            {[1, 2, 3, 4, 5].map((star) => (
              <div key={star} className="text-center p-4 bg-amber-50 rounded-xl">
                <div className="flex justify-center mb-2">
                  {[...Array(star)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-amber-400 fill-amber-400" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-2xl font-bold text-amber-600">{ratingStats.byRating[star] || 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-1">
          Valor en Pipeline Activo
          <InfoTooltip content="Suma del valor estimado de todos los leads que están en etapas activas (no cerrados ni perdidos). Representa el potencial de ingresos en negociación actualmente." />
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold" style={{ color: primaryColor }}>{formatCurrency(stats?.pipelineValue, true)}</p>
            <p className="text-sm text-slate-500 mt-1">Oportunidades en proceso</p>
          </div>
          {compareMode && comparisonStats && (
            <div className="text-right">
              <p className="text-sm text-slate-500">Período anterior</p>
              <p className="text-xl font-semibold text-slate-600">{formatCurrency(comparisonStats.pipelineValue, true)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Estadísticas por Comercial */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5" style={{ color: primaryColor }} />
            Rendimiento por Comercial
          </h3>
          <p className="text-sm text-slate-500 mt-1">Estadísticas de leads y negocios por usuario</p>
        </div>
        
        {loadingCommercials ? (
          <div className="p-8 text-center text-slate-500">
            <div className="animate-spin w-8 h-8 border-4 border-slate-200 border-t-indigo-600 rounded-full mx-auto mb-3"></div>
            Cargando estadísticas...
          </div>
        ) : commercialStats.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No hay datos de comerciales
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Comercial</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Leads Asignados</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">En Proceso</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Ganados</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Perdidos</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-slate-500 uppercase">Conversión</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase">Valor Ganado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {commercialStats.map((commercial, idx) => (
                  <tr key={commercial.userId} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {commercial.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{commercial.name}</p>
                          <p className="text-xs text-slate-500">{commercial.role === 'admin' ? 'Admin' : 'Comercial'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                        {commercial.totalLeadsAssigned}
                      </span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {commercial.dealsInProgress}
                      </span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {commercial.dealsWon}
                      </span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                        {commercial.dealsLost}
                      </span>
                    </td>
                    <td className="text-center px-3 py-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                        commercial.conversionRate >= 50 
                          ? 'bg-green-100 text-green-800' 
                          : commercial.conversionRate >= 25 
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-slate-100 text-slate-600'
                      }`}>
                        {commercial.conversionRate}%
                      </span>
                    </td>
                    <td className="text-right px-5 py-3 font-semibold text-slate-900">
                      {formatCurrency(commercial.totalValueWon, true)}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totals row */}
              <tfoot className="bg-slate-100 border-t-2 border-slate-200">
                <tr>
                  <td className="px-5 py-3 font-bold text-slate-900">TOTAL</td>
                  <td className="text-center px-3 py-3 font-bold text-indigo-600">
                    {commercialStats.reduce((sum, c) => sum + c.totalLeadsAssigned, 0)}
                  </td>
                  <td className="text-center px-3 py-3 font-bold text-blue-600">
                    {commercialStats.reduce((sum, c) => sum + c.dealsInProgress, 0)}
                  </td>
                  <td className="text-center px-3 py-3 font-bold text-green-600">
                    {commercialStats.reduce((sum, c) => sum + c.dealsWon, 0)}
                  </td>
                  <td className="text-center px-3 py-3 font-bold text-red-600">
                    {commercialStats.reduce((sum, c) => sum + c.dealsLost, 0)}
                  </td>
                  <td className="text-center px-3 py-3 font-bold text-slate-700">
                    {(() => {
                      const totalWon = commercialStats.reduce((sum, c) => sum + c.dealsWon, 0);
                      const totalLost = commercialStats.reduce((sum, c) => sum + c.dealsLost, 0);
                      const total = totalWon + totalLost;
                      return total > 0 ? `${Math.round(totalWon / total * 100)}%` : '-';
                    })()}
                  </td>
                  <td className="text-right px-5 py-3 font-bold text-green-600">
                    {formatCurrency(commercialStats.reduce((sum, c) => sum + c.totalValueWon, 0), true)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      </>
      ) : activeTab === 'cierres' ? (
        /* Cierres Reales Tab Content */
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
              <History className="w-6 h-6" style={{ color: primaryColor }} />
              Historial de Cierres Reales
            </h3>
            <div className="flex gap-2 flex-wrap">
              {[
                { id: 'resumen', label: 'Resumen' },
                { id: 'servicio', label: 'Por Servicio' },
                { id: 'cliente', label: 'Por Cliente' },
                { id: 'comercial', label: 'Por Comercial' },
              ].map(st => (
                <button key={st.id} onClick={() => { setCierresSubTab(st.id); setWonDealsPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${cierresSubTab === st.id ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  style={cierresSubTab === st.id ? { backgroundColor: primaryColor } : {}}>
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          {cierresSubTab === 'resumen' ? (
          <>
          {/* Monthly Closures Stats */}
          {monthlyClosures.length > 0 ? (
            <>
              {/* KPIs for closures */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-green-50">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {monthlyClosures.reduce((sum, m) => sum + m.count, 0)}
                  </p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Total Negocios Ganados
                    <InfoTooltip content="Número total de negocios con status='won' cerrados en el período. Se cuenta por la fecha de cierre real (actualCloseDate), no por fecha de creación." />
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-blue-50">
                      <DollarSign className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                    {formatCurrency(monthlyClosures.reduce((sum, m) => sum + m.value, 0), true)}
                  </p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Valor Total Ganado
                    <InfoTooltip content="Suma del valor (totalValue) de todos los negocios ganados en el período seleccionado. Incluye todos los servicios asociados a cada negocio." />
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-purple-50">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  {(() => {
                    const totalValue = monthlyClosures.reduce((sum, m) => sum + m.value, 0);
                    const totalCount = monthlyClosures.reduce((sum, m) => sum + m.count, 0);
                    const avgValue = totalCount > 0 ? totalValue / totalCount : 0;
                    return (
                      <Tooltip content={formatFullCurrency(avgValue)}>
                        <p className="text-2xl font-bold text-purple-600 cursor-help border-b border-dotted border-purple-300 inline-block">
                          {formatCurrency(avgValue)}
                        </p>
                      </Tooltip>
                    );
                  })()}
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Valor Promedio por Negocio
                    <InfoTooltip content="Fórmula: Valor Total Ganado / Total Negocios Ganados. Representa el ticket promedio de cada negocio cerrado." />
                  </p>
                </div>
              </div>

              {/* Chart */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: primaryColor }} />
                  Cierres por Mes
                  <InfoTooltip content="Gráfico que muestra la cantidad de negocios cerrados (ganados) y su valor total por cada mes. El número dentro de la barra indica la cantidad de negocios." />
                </h4>
                <div className="space-y-3">
                  {monthlyClosures.slice(-12).map((item, index) => {
                    const maxValue = Math.max(...monthlyClosures.map(m => m.value));
                    const monthName = new Date(item.month + '-01').toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <span className="text-sm text-slate-600 w-16 font-medium uppercase">{monthName}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-8 overflow-hidden relative">
                          <div
                            className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                            style={{ 
                              width: `${Math.max((item.value / maxValue) * 100, 10)}%`,
                              backgroundColor: primaryColor
                            }}
                          >
                            <span className="text-white text-xs font-medium">{item.count}</span>
                          </div>
                        </div>
                        <div className="w-28 text-right">
                          <Tooltip content={formatFullCurrency(item.value)}>
                            <span className="text-sm font-bold text-slate-900 cursor-help border-b border-dotted border-slate-400">
                              {formatCurrency(item.value)}
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-200">
                  <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    Detalle Mensual
                    <InfoTooltip content="Tabla con el desglose mensual: cantidad de negocios cerrados, valor total y valor promedio por negocio en cada mes." />
                  </h4>
                </div>
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Mes</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Negocios</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Valor Total</th>
                      <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Promedio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {monthlyClosures.slice(-12).reverse().map((item, index) => {
                      const monthName = new Date(item.month + '-01').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
                      const avg = item.count > 0 ? item.value / item.count : 0;
                      return (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="px-6 py-3 text-sm font-medium text-slate-900 capitalize">{monthName}</td>
                          <td className="px-6 py-3 text-sm text-right font-semibold text-slate-900">{item.count}</td>
                          <td className="px-6 py-3 text-sm text-right font-semibold" style={{ color: primaryColor }}>
                            {formatCurrency(item.value, true)}
                          </td>
                          <td className="px-6 py-3 text-sm text-right text-slate-600">{formatCurrency(avg)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Service Stats */}
              {serviceStats.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5" style={{ color: primaryColor }} />
                    Cierres por Servicio
                    <InfoTooltip content="Desglose de negocios ganados por tipo de servicio vendido. Muestra cuántas veces se vendió cada servicio, el valor total y el promedio." />
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Servicio</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Cantidad</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocios</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Valor Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {serviceStats.map((service, idx) => {
                          const maxValue = serviceStats[0]?.value || 1;
                          return (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                    idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                    idx === 1 ? 'bg-slate-100 text-slate-700' :
                                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                                    'bg-slate-50 text-slate-500'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                  <span className="text-sm font-medium text-slate-900">{service.name}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm font-semibold text-slate-900">{service.count}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-sm text-slate-600">{service.deals}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-20 bg-slate-100 rounded-full h-2 overflow-hidden">
                                    <div
                                      className="h-full rounded-full"
                                      style={{ 
                                        width: `${(service.value / maxValue) * 100}%`,
                                        backgroundColor: primaryColor
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm font-semibold" style={{ color: primaryColor }}>
                                    {formatCurrency(service.value, true)}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 font-semibold">
                        <tr>
                          <td className="px-4 py-3 text-sm text-slate-900">Total</td>
                          <td className="px-4 py-3 text-right text-sm text-slate-900">
                            {serviceStats.reduce((sum, s) => sum + s.count, 0)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-slate-900">
                            {serviceStats.reduce((sum, s) => sum + s.deals, 0)}
                          </td>
                          <td className="px-4 py-3 text-right text-sm" style={{ color: primaryColor }}>
                            {formatCurrency(serviceStats.reduce((sum, s) => sum + s.value, 0), true)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <History className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Sin cierres registrados</h3>
              <p className="text-slate-500">
                Los cierres aparecerán aquí cuando registres negocios como "Ganados" en la sección de Negocios.
              </p>
            </div>
          )}
          </>
          ) : (
          /* Sub-tabs: Por Servicio, Por Cliente, Por Comercial */
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loadingWonDeals ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full" style={{ borderColor: primaryColor, borderTopColor: 'transparent' }}></div>
              </div>
            ) : wonDeals.length === 0 ? (
              <div className="p-12 text-center">
                <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay cierres en este período</p>
              </div>
            ) : (
              <>
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                  <p className="text-sm text-slate-500">{wonDealsTotal} negocio(s) ganado(s)</p>
                </div>
                {(() => {
                  if (cierresSubTab === 'servicio') {
                    const serviceMap = {};
                    wonDeals.forEach(d => {
                      (d.services || []).forEach(s => {
                        const name = s.name || s.serviceName || 'Sin servicio';
                        if (!serviceMap[name]) serviceMap[name] = { count: 0, value: 0, deals: [] };
                        serviceMap[name].count++;
                        serviceMap[name].value += s.value || s.price || 0;
                        serviceMap[name].deals.push(d);
                      });
                      if (!d.services || d.services.length === 0) {
                        const name = 'Sin servicio';
                        if (!serviceMap[name]) serviceMap[name] = { count: 0, value: 0, deals: [] };
                        serviceMap[name].count++;
                        serviceMap[name].value += d.totalValue || 0;
                        serviceMap[name].deals.push(d);
                      }
                    });
                    const sorted = Object.entries(serviceMap).sort((a, b) => b[1].value - a[1].value);
                    return (
                      <table className="w-full">
                        <thead className="bg-slate-50"><tr>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Servicio</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Negocios</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Valor Total</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {sorted.map(([name, data], i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="px-6 py-3 text-sm font-medium text-slate-900">{name}</td>
                              <td className="px-6 py-3 text-sm text-right font-semibold">{data.count}</td>
                              <td className="px-6 py-3 text-sm text-right font-semibold" style={{ color: primaryColor }}>{formatCurrency(data.value, true)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }
                  if (cierresSubTab === 'cliente') {
                    const clientMap = {};
                    wonDeals.forEach(d => {
                      const name = d.empresaName || d.leadName || 'Sin cliente';
                      if (!clientMap[name]) clientMap[name] = { count: 0, value: 0 };
                      clientMap[name].count++;
                      clientMap[name].value += d.totalValue || 0;
                    });
                    const sorted = Object.entries(clientMap).sort((a, b) => b[1].value - a[1].value);
                    return (
                      <table className="w-full">
                        <thead className="bg-slate-50"><tr>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Cliente / Empresa</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Negocios</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Valor Total</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {sorted.map(([name, data], i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="px-6 py-3 text-sm font-medium text-slate-900">{name}</td>
                              <td className="px-6 py-3 text-sm text-right font-semibold">{data.count}</td>
                              <td className="px-6 py-3 text-sm text-right font-semibold" style={{ color: primaryColor }}>{formatCurrency(data.value, true)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }
                  if (cierresSubTab === 'comercial') {
                    const commMap = {};
                    wonDeals.forEach(d => {
                      const name = d.commercialName || 'Sin asignar';
                      if (!commMap[name]) commMap[name] = { count: 0, value: 0 };
                      commMap[name].count++;
                      commMap[name].value += d.totalValue || 0;
                    });
                    const sorted = Object.entries(commMap).sort((a, b) => b[1].value - a[1].value);
                    return (
                      <table className="w-full">
                        <thead className="bg-slate-50"><tr>
                          <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Comercial</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Negocios</th>
                          <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Valor Total</th>
                        </tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {sorted.map(([name, data], i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="px-6 py-3 text-sm font-medium text-slate-900">{name}</td>
                              <td className="px-6 py-3 text-sm text-right font-semibold">{data.count}</td>
                              <td className="px-6 py-3 text-sm text-right font-semibold" style={{ color: primaryColor }}>{formatCurrency(data.value, true)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  }
                  return null;
                })()}
                {/* Pagination */}
                {wonDealsTotalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200">
                    <p className="text-sm text-slate-500">Página {wonDealsPage} de {wonDealsTotalPages}</p>
                    <div className="flex gap-2">
                      <button disabled={wonDealsPage <= 1} onClick={() => setWonDealsPage(p => p - 1)}
                        className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50">Anterior</button>
                      <button disabled={wonDealsPage >= wonDealsTotalPages} onClick={() => setWonDealsPage(p => p + 1)}
                        className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50">Siguiente</button>
                    </div>
                  </div>
                )}

                {/* Full deals list */}
                <div className="px-6 py-4 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Detalle de Negocios ({wonDeals.length})</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50"><tr>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Negocio</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Cliente</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Comercial</th>
                        <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500">Servicios</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Valor</th>
                        <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500">Fecha Cierre</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {wonDeals.map(d => (
                          <tr key={d.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2 font-medium text-slate-900">{d.title || d.leadName}</td>
                            <td className="px-4 py-2 text-slate-600">{d.empresaName || d.leadName || '-'}</td>
                            <td className="px-4 py-2 text-slate-600">{d.commercialName}</td>
                            <td className="px-4 py-2">
                              {(d.services || []).map((s, i) => (
                                <span key={i} className="inline-block bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded mr-1 mb-0.5">{s.name || s.serviceName}</span>
                              ))}
                            </td>
                            <td className="px-4 py-2 text-right font-semibold" style={{ color: primaryColor }}>{formatCurrency(d.totalValue || 0, true)}</td>
                            <td className="px-4 py-2 text-right text-slate-500">{d.actualCloseDate ? new Date(d.actualCloseDate).toLocaleDateString('es-CO') : d.updatedAt ? new Date(d.updatedAt).toLocaleDateString('es-CO') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
          )}
        </div>
      ) : activeTab === 'patrones' ? (
        /* Patrones Tab Content */
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Activity className="w-6 h-6" style={{ color: primaryColor }} />
              Patrones de Comportamiento
            </h3>
            <p className="text-slate-500 text-sm">
              Análisis de patrones de compra y conversión para identificar oportunidades de mejora
            </p>
          </div>

          {loadingPatterns ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-slate-200 border-t-indigo-600 rounded-full mx-auto mb-4"></div>
              <p className="text-slate-500">Analizando patrones...</p>
            </div>
          ) : behaviorPatterns ? (
            <>
              {/* Time Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-blue-50">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                    {behaviorPatterns.timeMetrics?.avgCloseTimeDays || 0} días
                  </p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Tiempo Promedio de Cierre
                    <InfoTooltip content="Promedio de días desde la creación del negocio hasta su cierre (status='won'). Solo incluye negocios ganados en el período." />
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Min: {behaviorPatterns.timeMetrics?.minCloseTimeDays || 0}d | Max: {behaviorPatterns.timeMetrics?.maxCloseTimeDays || 0}d
                  </p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-green-50">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(behaviorPatterns.valueMetrics?.avgPurchaseValue || 0, true)}
                  </p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Ticket Promedio
                    <InfoTooltip content="Valor promedio de cada negocio ganado. Fórmula: Suma de valores / Número de negocios ganados." />
                  </p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-purple-50">
                      <Trophy className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">
                    {behaviorPatterns.conversionMetrics?.winRate || 0}%
                  </p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Tasa de Conversión
                    <InfoTooltip content="Porcentaje de negocios ganados vs total de negocios cerrados (ganados + perdidos) en el período." />
                  </p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-orange-50">
                      <ShoppingBag className="w-5 h-5 text-orange-600" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-orange-600">
                    {behaviorPatterns.conversionMetrics?.avgServicesPerDeal?.toFixed(1) || 0}
                  </p>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1">
                    Servicios por Negocio
                    <InfoTooltip content="Promedio de servicios incluidos en cada negocio ganado. Mayor número indica ventas más completas." />
                  </p>
                </div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Services */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5" style={{ color: primaryColor }} />
                    Servicios Más Vendidos
                    <InfoTooltip content="Ranking de servicios por cantidad vendida en el período. Incluye solo negocios con status='won'." />
                  </h4>
                  <div className="space-y-3">
                    {behaviorPatterns.topServices?.slice(0, 6).map((service, idx) => {
                      const maxCount = behaviorPatterns.topServices[0]?.count || 1;
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                            idx === 1 ? 'bg-slate-100 text-slate-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-50 text-slate-500'
                          }`}>
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-900 truncate">{service.name}</span>
                              <span className="text-sm font-semibold text-slate-700">{service.count}</span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2">
                              <div
                                className="h-2 rounded-full transition-all"
                                style={{ 
                                  width: `${(service.count / maxCount) * 100}%`,
                                  backgroundColor: primaryColor
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Best Lead Sources */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5" style={{ color: primaryColor }} />
                    Fuentes con Mayor Conversión
                    <InfoTooltip content="Fuentes de leads ordenadas por tasa de conversión. Muestra qué canales generan más ventas exitosas." />
                  </h4>
                  <div className="space-y-3">
                    {behaviorPatterns.bestSources?.slice(0, 6).map((source, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-2 h-2 rounded-full ${
                            source.conversionRate >= 20 ? 'bg-green-500' :
                            source.conversionRate >= 10 ? 'bg-yellow-500' :
                            'bg-slate-400'
                          }`} />
                          <span className="text-sm font-medium text-slate-900">{source.source}</span>
                        </div>
                        <div className="text-right">
                          <span className={`text-sm font-bold ${
                            source.conversionRate >= 20 ? 'text-green-600' :
                            source.conversionRate >= 10 ? 'text-yellow-600' :
                            'text-slate-500'
                          }`}>
                            {source.conversionRate}%
                          </span>
                          <p className="text-xs text-slate-400">{source.won}/{source.total} ganados</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Stage Funnel */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5" style={{ color: primaryColor }} />
                  Embudo de Conversión por Etapa
                  <InfoTooltip content="Porcentaje de leads que pasaron por cada etapa del embudo. Muestra la tasa de avance entre etapas desde la primera hasta el cierre." />
                </h4>
                <div className="flex items-end gap-2 h-48">
                  {behaviorPatterns.stageFunnel?.filter(s => !['perdido'].includes(s.stage)).map((stage, idx) => {
                    const heightPercent = Math.max(stage.percentage, 5);
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <span className="text-xs font-semibold text-slate-700 mb-1">{stage.percentage}%</span>
                        <div
                          className="w-full rounded-t-lg transition-all"
                          style={{ 
                            height: `${heightPercent}%`,
                            backgroundColor: stage.stage === 'cerrado' ? '#22C55E' : primaryColor,
                            opacity: 1 - (idx * 0.15)
                          }}
                        />
                        <span className="text-xs text-slate-600 mt-2 text-center">{stage.name}</span>
                        <span className="text-xs text-slate-400">{stage.passedThrough}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Deal Size Trend */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: primaryColor }} />
                  Tendencia del Valor Promedio por Negocio
                  <InfoTooltip content="Evolución mensual del ticket promedio de los negocios ganados. Permite identificar tendencias de aumento o disminución del valor por negocio." />
                </h4>
                <div className="space-y-3">
                  {behaviorPatterns.dealSizeTrend?.slice(-12).map((item, idx) => {
                    const maxValue = Math.max(...(behaviorPatterns.dealSizeTrend?.map(d => d.avgValue) || [1]));
                    const monthName = new Date(item.month + '-01').toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-xs text-slate-600 w-16 font-medium uppercase">{monthName}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden relative">
                          <div
                            className="h-full rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max((item.avgValue / maxValue) * 100, 10)}%`,
                              backgroundColor: primaryColor
                            }}
                          >
                            <span className="text-white text-xs font-medium">{item.dealCount}</span>
                          </div>
                        </div>
                        <div className="w-24 text-right">
                          <Tooltip content={formatFullCurrency(item.avgValue)}>
                            <span className="text-sm font-semibold text-slate-900 cursor-help border-b border-dotted border-slate-400">
                              {formatCurrency(item.avgValue)}
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="w-6 h-6 text-green-600" />
                    <span className="text-sm font-medium text-green-700 flex items-center gap-1">
                      Total Valor Ganado
                      <InfoTooltip content="Suma total del valor de todos los negocios ganados en el período seleccionado." />
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    {formatCurrency(behaviorPatterns.valueMetrics?.totalWonValue || 0, true)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Repeat className="w-6 h-6 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700 flex items-center gap-1">
                      Clientes Recurrentes
                      <InfoTooltip content="Empresas que tienen más de un negocio ganado. Indica fidelización de clientes." />
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-blue-600">
                    {behaviorPatterns.conversionMetrics?.repeatCustomers || 0}
                  </p>
                  <p className="text-xs text-blue-500">
                    de {behaviorPatterns.conversionMetrics?.totalCustomers || 0} clientes totales
                  </p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-6 h-6 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700 flex items-center gap-1">
                      Negocios Ganados
                      <InfoTooltip content="Total de negocios con status 'ganado' cerrados en el período analizado." />
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-purple-600">
                    {behaviorPatterns.timeMetrics?.totalClosedDeals || 0}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Activity className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Sin datos suficientes</h3>
              <p className="text-slate-500">
                Se necesitan más negocios ganados para analizar los patrones de comportamiento.
              </p>
            </div>
          )}
        </div>
      ) : activeTab === 'tracking' ? (
        <div className="space-y-6">
          {loadingTracking ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
            </div>
          ) : trackingStats && (trackingStats.channels?.length > 0 || trackingStats.devices?.length > 0) ? (
            <>
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-3">
                  <div className="bg-emerald-100 p-3 rounded-xl">
                    <Target className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-emerald-900">Origen de Leads</h3>
                    <p className="text-sm text-emerald-700">
                      Análisis de canales, dispositivos y tipos de email de los leads capturados
                    </p>
                  </div>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Globe className="w-5 h-5 text-blue-500" />
                    <span className="text-sm text-slate-600">Canales Únicos</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{trackingStats.channels?.length || 0}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Monitor className="w-5 h-5 text-purple-500" />
                    <span className="text-sm text-slate-600">Tipos de Dispositivo</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{trackingStats.devices?.length || 0}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <span className="text-sm text-slate-600">Tiempo Promedio</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{trackingStats.avgTimeOnPage || 0}s</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="text-sm text-slate-600">Bots Detectados</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{trackingStats.botDetection?.bots || 0}</p>
                </div>
              </div>

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By Channel */}
                {trackingStats.channels?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-blue-50 px-5 py-3 border-b border-blue-100 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-900">Leads por Canal</h4>
                    </div>
                    <div className="p-5 space-y-3">
                      {trackingStats.channels.slice(0, 8).map((item, idx) => {
                        const maxCount = Math.max(...trackingStats.channels.map(c => c.count));
                        const pct = (item.count / maxCount) * 100;
                        const colors = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];
                        return (
                          <div key={idx}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700 truncate max-w-[70%]">
                                {item.name || 'Sin canal'}
                              </span>
                              <span className="text-sm font-bold text-slate-900">{item.count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${pct}%`, backgroundColor: colors[idx % colors.length] }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* By Device */}
                {trackingStats.devices?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-purple-50 px-5 py-3 border-b border-purple-100 flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-purple-900">Leads por Dispositivo</h4>
                    </div>
                    <div className="p-5">
                      <div className="grid grid-cols-3 gap-4">
                        {trackingStats.devices.map((item, idx) => {
                          const total = trackingStats.devices.reduce((acc, d) => acc + d.count, 0);
                          const pct = ((item.count / total) * 100).toFixed(1);
                          const icons = {
                            desktop: <Monitor className="w-8 h-8" />,
                            mobile: <Smartphone className="w-8 h-8" />,
                            tablet: <Tablet className="w-8 h-8" />
                          };
                          const colors = {
                            desktop: 'text-blue-500 bg-blue-50',
                            mobile: 'text-green-500 bg-green-50',
                            tablet: 'text-orange-500 bg-orange-50'
                          };
                          return (
                            <div key={idx} className="text-center p-4 rounded-xl bg-slate-50">
                              <div className={`inline-flex p-3 rounded-xl mb-2 ${colors[item.name] || 'text-slate-500 bg-slate-100'}`}>
                                {icons[item.name] || <Monitor className="w-8 h-8" />}
                              </div>
                              <p className="text-2xl font-bold text-slate-900">{item.count}</p>
                              <p className="text-sm text-slate-500 capitalize">{item.name || 'Otro'}</p>
                              <p className="text-xs text-slate-400">{pct}%</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* By Email Type */}
                {trackingStats.emailTypes?.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-orange-50 px-5 py-3 border-b border-orange-100 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-orange-600" />
                      <h4 className="font-semibold text-orange-900">Leads por Tipo de Email</h4>
                    </div>
                    <div className="p-5">
                      <div className="grid grid-cols-2 gap-4">
                        {trackingStats.emailTypes.map((item, idx) => {
                          const total = trackingStats.emailTypes.reduce((acc, e) => acc + e.count, 0);
                          const pct = ((item.count / total) * 100).toFixed(1);
                          const isGeneric = item.name === 'generico';
                          return (
                            <div key={idx} className={`p-4 rounded-xl ${isGeneric ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-2 rounded-lg ${isGeneric ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                                  <Mail className={`w-5 h-5 ${isGeneric ? 'text-amber-600' : 'text-emerald-600'}`} />
                                </div>
                                <span className={`text-sm font-medium ${isGeneric ? 'text-amber-800' : 'text-emerald-800'}`}>
                                  {isGeneric ? 'Email Genérico' : 'Email Corporativo'}
                                </span>
                              </div>
                              <p className={`text-3xl font-bold ${isGeneric ? 'text-amber-700' : 'text-emerald-700'}`}>{item.count}</p>
                              <p className="text-sm text-slate-500">{pct}% del total</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* By Source - Combined from channels and utm_source */}
                {(trackingStats.channels?.length > 0 || trackingStats.sources?.length > 0) && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="bg-green-50 px-5 py-3 border-b border-green-100 flex items-center gap-2">
                      <Tag className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-900">Leads por Fuente</h4>
                    </div>
                    <div className="p-5 space-y-3">
                      {(() => {
                        // Combine channels and sources, prioritize channels
                        const combinedData = [...(trackingStats.channels || [])];
                        // Add sources that aren't already represented in channels
                        (trackingStats.sources || []).forEach(src => {
                          if (!combinedData.some(c => c.name?.toLowerCase() === src.name?.toLowerCase())) {
                            combinedData.push(src);
                          }
                        });
                        const maxCount = Math.max(...combinedData.map(s => s.count), 1);
                        
                        return combinedData.slice(0, 8).map((item, idx) => {
                          const pct = (item.count / maxCount) * 100;
                          return (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-slate-700">{item.name || 'Sin fuente'}</span>
                                <span className="text-sm font-bold text-slate-900">{item.count}</span>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full bg-green-500 transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* By Campaign */}
              {trackingStats.campaigns?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-indigo-50 px-5 py-3 border-b border-indigo-100 flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-600" />
                    <h4 className="font-semibold text-indigo-900">Leads por Campaña (utm_campaign)</h4>
                  </div>
                  <div className="p-5 space-y-3">
                    {trackingStats.campaigns.slice(0, 8).map((item, idx) => {
                      const maxCount = Math.max(...trackingStats.campaigns.map(c => c.count));
                      const pct = (item.count / maxCount) * 100;
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700 truncate max-w-[70%]">{item.name || 'Sin campaña'}</span>
                            <span className="text-sm font-bold text-slate-900">{item.count}</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Sin datos de tracking</h3>
              <p className="text-slate-500">
                Los datos de origen se capturan cuando los leads llenan formularios con el script de tracking instalado.
              </p>
            </div>
          )}
        </div>
      ) : activeTab === 'perdidas' ? (
        <div className="space-y-6" data-testid="loss-reasons-content">
          {loadingLossReasons ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : lossReasonStats ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-red-50">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-500">Total Perdidos</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">{lossReasonStats.totalLost || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">{periodLabels[dateRange]}</p>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-orange-50">
                      <DollarSign className="w-5 h-5 text-orange-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-500">Valor Perdido</span>
                  </div>
                  <p className="text-3xl font-bold text-slate-900">
                    {new Intl.NumberFormat('es-CO', { notation: 'compact', maximumFractionDigits: 1 }).format(lossReasonStats.totalValueLost || 0)} {currency}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Oportunidades no concretadas</p>
                </div>
                
                <div className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-purple-50">
                      <PieChart className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-500">Razón Principal</span>
                  </div>
                  <p className="text-xl font-bold text-slate-900 truncate">
                    {lossReasonStats.reasons?.[0]?.label || 'Sin datos'}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {lossReasonStats.reasons?.[0]?.percentage || 0}% de los casos
                  </p>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie Chart / Distribution */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-red-50 px-5 py-3 border-b border-red-100 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-red-600" />
                    <h4 className="font-semibold text-red-900">Distribución de Razones</h4>
                  </div>
                  <div className="p-5">
                    {lossReasonStats.reasons?.length > 0 ? (
                      <div className="space-y-4">
                        {/* Visual Pie Chart using CSS */}
                        <div className="flex justify-center mb-6">
                          <div className="relative w-48 h-48">
                            <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                              {(() => {
                                const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6'];
                                let accumulated = 0;
                                return lossReasonStats.reasons.map((reason, idx) => {
                                  const percentage = reason.percentage || 0;
                                  const dashArray = `${percentage} ${100 - percentage}`;
                                  const dashOffset = -accumulated;
                                  accumulated += percentage;
                                  return (
                                    <circle
                                      key={idx}
                                      cx="50"
                                      cy="50"
                                      r="40"
                                      fill="none"
                                      stroke={colors[idx % colors.length]}
                                      strokeWidth="20"
                                      strokeDasharray={dashArray}
                                      strokeDashoffset={dashOffset}
                                      className="transition-all duration-500"
                                    />
                                  );
                                });
                              })()}
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <p className="text-2xl font-bold text-slate-900">{lossReasonStats.totalLost}</p>
                                <p className="text-xs text-slate-500">Total</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Legend */}
                        <div className="space-y-2">
                          {(() => {
                            const colors = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#3B82F6', '#8B5CF6'];
                            return lossReasonStats.reasons.map((reason, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                                <div className="flex items-center gap-3">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: colors[idx % colors.length] }}
                                  />
                                  <span className="text-sm font-medium text-slate-700">{reason.label}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-bold text-slate-900">{reason.count}</span>
                                  <span className="text-xs text-slate-500 w-12 text-right">{reason.percentage}%</span>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No hay leads perdidos en este período</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bar Chart / Value by Reason */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-orange-50 px-5 py-3 border-b border-orange-100 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                    <h4 className="font-semibold text-orange-900">Valor Perdido por Razón</h4>
                  </div>
                  <div className="p-5">
                    {lossReasonStats.reasons?.length > 0 ? (
                      <div className="space-y-4">
                        {(() => {
                          const maxValue = Math.max(...lossReasonStats.reasons.map(r => r.value || 0));
                          return lossReasonStats.reasons.map((reason, idx) => {
                            const pct = maxValue > 0 ? (reason.value / maxValue) * 100 : 0;
                            return (
                              <div key={idx}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-slate-700 truncate max-w-[60%]">{reason.label}</span>
                                  <span className="text-sm font-bold text-slate-900">
                                    {new Intl.NumberFormat('es-CO', { notation: 'compact', maximumFractionDigits: 1 }).format(reason.value || 0)} {currency}
                                  </span>
                                </div>
                                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full bg-gradient-to-r from-orange-400 to-red-500 transition-all duration-500"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">Sin datos de valor</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent Lost Leads by Reason */}
              {lossReasonStats.reasons?.some(r => r.recentLeads?.length > 0) && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-600" />
                    <h4 className="font-semibold text-slate-900">Leads Perdidos Recientes</h4>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {lossReasonStats.reasons.filter(r => r.recentLeads?.length > 0).map((reason, idx) => (
                        <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-red-50 px-3 py-2 border-b border-red-100">
                            <p className="text-sm font-semibold text-red-800 truncate">{reason.label}</p>
                          </div>
                          <div className="p-3 space-y-2">
                            {reason.recentLeads.slice(0, 3).map((lead, lidx) => (
                              <div key={lidx} className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-900 truncate">{lead.name}</p>
                                  {lead.company && (
                                    <p className="text-xs text-slate-500 truncate">{lead.company}</p>
                                  )}
                                </div>
                                <span className="text-xs font-semibold text-red-600 whitespace-nowrap ml-2">
                                  {new Intl.NumberFormat('es-CO', { notation: 'compact' }).format(lead.value || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Pipeline Breakdown (only shown when "all" is selected) */}
              {lossReasonStats.pipelineBreakdown?.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-purple-50 px-5 py-3 border-b border-purple-100 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-purple-900">Pérdidas por Embudo</h4>
                  </div>
                  <div className="p-5">
                    <div className="space-y-3">
                      {(() => {
                        const maxCount = Math.max(...lossReasonStats.pipelineBreakdown.map(p => p.count));
                        return lossReasonStats.pipelineBreakdown.map((pipeline, idx) => {
                          const pct = (pipeline.count / maxCount) * 100;
                          return (
                            <div key={idx}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-slate-700">{pipeline.pipelineName}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-bold text-slate-900">{pipeline.count} leads</span>
                                  <span className="text-xs text-slate-500">
                                    {new Intl.NumberFormat('es-CO', { notation: 'compact' }).format(pipeline.value || 0)} {currency}
                                  </span>
                                </div>
                              </div>
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full bg-purple-500 transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Sin datos de pérdidas</h3>
              <p className="text-slate-500">
                Las razones de pérdida se registran cuando un lead se mueve al estado "Perdido".
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default Statistics;
